import Select, { InputActionMeta } from 'react-select'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { FramesManager } from 'shared-libs/components/view/frames-manager'

export type ReactSelectProps = React.ComponentProps<typeof Select>
export type SelectStyleProps = ReactSelectProps['styles']
export type SelectComponentProps = ReactSelectProps['components']

/**
 * @uiComponent
 */
export interface ISelectProps extends IBaseProps {
  frames?: FramesManager // TODO: move to IBaseProps, if safe
  autoFocus?: boolean
  dir?: string
  errorText?: string
  hasError?: boolean
  formula?: string
  labelFormula?: string
  isDisabledFormula?: string
  isHiddenFormula?: string
  isHiddenOptionsFormula?: string
  isHiddenOptionFn?: (option: any, inputValue: string | string[]) => boolean
  inputClassName?: string
  isDisabled?: boolean
  isReadOnly?: boolean
  isLoading?: boolean
  isRequired?: boolean
  isErrorInline?: boolean
  isHorizontalLayout?: boolean
  isStatic?: boolean
  menuRenderer?: (option: any) => void
  onChange?: (value: any, option?: any, silentUpdate?: any, overridePath?: any, actionMeta?: any) => void
  onInputChange?: (value: string, actionMeta?: InputActionMeta) => void
  onNewOptionClick?: (label: string) => void
  onSelectRef?: (ref: any) => void
  optionHeight?: number
  optionRenderer?: (option: any) => JSX.Element
  isAsync?: boolean
  isEditableInline?: boolean
  isEditableLabel?: boolean
  isNative?: boolean
  isValidNewOption?: (any) => boolean
  onFocus?: (event) => void
  onBlur?: (event) => void
  size?: string
  value?: any
  validator?: (option: any) => boolean
  onOpen?: () => void
  onMenuClose?: () => void
  // the default filter only filters by value and label
  filterByFieldPaths?: string[]

  // React Select Props
  onSelectMenuChange?: any
  inputRenderer?: (inputProps: any) => JSX.Element
  optionMapper?: (option: any, inputValue?: string) => any
  isCreatable?: boolean
  showCreatableWithResults?: boolean
  clearable?: boolean
  isClearable?: boolean
  loadingPlaceholder?: string
  multi?: boolean
  promptTextCreator?: (label: string) => string
  menuIsOpen?: boolean | undefined
  noResultsText?: string
  optionGroupPath?: string
  optionLabelPath?: string
  optionValuePath?: string
  options?: any[]
  searchable?: boolean
  isSearchable?: boolean
  searchPromptText?: string
  tabIndex?: string
  placeholder?: string
  noOptionsMessage?: string
  formatOptionLabel?: (option: any) => JSX.Element
  formatGroupLabel?: (group: any) => JSX.Element
  valueRenderer?: (option: any) => JSX.Element
  filterOption?: (option: any) => boolean
  openMenuOnFocus?: boolean
  openMenuOnClick?: boolean
  controlShouldRenderValue?: boolean
  lockedValues?: any[]
  minMenuHeight?: number

  showRemoveOption?: boolean
  removeOptionText?: string

  styles?: SelectStyleProps
  components?: SelectComponentProps

  // Multi-select mode:
  // 'checkbox' — large async datasets; keeps menu open, shows search input, checkbox options,
  //              batches selections as draft and commits on menu close (used by entity-select).
  // 'instant'  — simple/freeform values; standard react-select multi behavior, menu closes on
  //              select, no custom menu or checkboxes.
  // When omitted, single-select behavior applies.
  multiMode?: 'checkbox' | 'instant'

  // Multi-select options
  allowSelectAll?: boolean
  isItemClearable?: boolean
  closeMenuOnSelect?: boolean
  hideSelectedOptions?: boolean
  groupOptionsFn?: (options: any[]) => Array<{ label: string; options: any[] }>
  groupOptionsFormula?: string
  preventDuplicateSelections?: boolean
}

export const defaultProps: Partial<ISelectProps> = {
  clearable: false,
  dir: 'ltr',
  isLoading: false,
  isStatic: false,
  loadingPlaceholder: 'Loading...',
  noResultsText: 'No results found',
  optionLabelPath: 'label',
  optionValuePath: 'value',
  options: [],
  placeholder: 'Select',
  searchPromptText: 'Type to search...',
  searchable: true,
  isSearchable: true,
  components: {},
}
